import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import React, { useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  resetValidation,
  setActiveStep,
} from "../../context/bulkUploadActions";
import { useBulkUpload } from "../../hooks/useBulkUpload";
import { useProcessing } from "../../hooks/useProcessing";
import { getProgramCourseFee } from "../../utils/programFee";
import { ResultsStep } from "./ResultsStep";
const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(6),
    textAlign: "center",
  },
  progressContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  progressBar: {
    height: 10,
    borderRadius: 5,
  },
  spinner: {
    marginBottom: theme.spacing(3),
  },
  operationText: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  warningBox: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(3),
    backgroundColor: "#fff3e0",
    borderRadius: theme.shape.borderRadius,
  },
}));

export const ProcessingStep = ({ programId, programDetails }) => {
  const classes = useStyles();
  const { state, dispatch } = useBulkUpload();
  const { processUpload } = useProcessing();

  const branch = useSelector(
    (state) => state.auth.branches.currentBranch,
    shallowEqual
  );
  const user = useSelector((state) => state.auth.user);

  const { processing } = state;
  const { course, fee, problem: feeProblem } = getProgramCourseFee(
    programDetails
  );

  useEffect(() => {
    // Start processing when component mounts -- never without a usable fee
    if (processing.status === "idle" && !feeProblem) {
      startProcessing();
    }
  }, []);

  const startProcessing = async () => {
    const success = await processUpload(
      programId,
      branch.branch_id,
      course.course_id,
      fee.branch_course_fee_id ?? null,
      fee.course_fee
    );

    // Auto-advance to results after processing completes
    if (success) {
      setTimeout(() => {
        // This will be handled by the results step
      }, 1000);
    }
  };

  const handleBack = () => {
    dispatch(resetValidation());
    dispatch(setActiveStep(0));
  };

  if (feeProblem && processing.status === "idle") {
    return (
      <Box className={classes.container}>
        <div className="alert alert-danger">
          <div className="alert-text">
            <strong>Can't process this upload</strong>
            <p className="mb-0 mt-2">{feeProblem}</p>
            <div className="d-flex justify-content-center mt-6">
              <button className="btn btn-light" onClick={handleBack}>
                Back
              </button>
            </div>
          </div>
        </div>
      </Box>
    );
  }

  // If processing complete, show results
  if (processing.status === "success") {
    return <ResultsStep />;
  }

  // If processing error
  if (processing.status === "error") {
    return (
      <Box className={classes.container}>
        <div className="alert alert-danger">
          <div className="alert-text">
            <strong>Processing Failed</strong>
            <p className="mb-0 mt-2">{state.error}</p>
            <div className="d-flex justify-content-center mt-6">
              <button className="btn btn-light" onClick={handleBack}>
                Back
              </button>
            </div>
          </div>
        </div>
      </Box>
    );
  }

  const { progress } = processing;

  return (
    <Box className={classes.container}>
      <CircularProgress size={80} className={classes.spinner} />

      <Typography variant="h4" gutterBottom>
        Processing Your Upload
      </Typography>

      <Typography variant="body1" color="textSecondary" gutterBottom>
        {progress.currentOperation}
      </Typography>

      <Box className={classes.progressContainer}>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="textSecondary">
            Progress
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {progress.current} / {progress.total} students
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress.percentage}
          className={classes.progressBar}
        />

        <Box mt={1} textAlign="center">
          <Typography variant="h5" color="primary">
            {progress.percentage}%
          </Typography>
        </Box>
      </Box>

      <Box className={classes.warningBox}>
        <Typography variant="body2">
          <i className="fa fa-info-circle mr-2"></i>
          <strong>Please don't close this window</strong> - Processing is in
          progress. This may take a few moments.
        </Typography>
      </Box>

      <Box mt={4}>
        <div className="timeline timeline-3">
          <div className="timeline-items">
            <div className="timeline-item">
              <div
                className={`timeline-media ${
                  progress.percentage >= 25 ? "bg-success" : "bg-secondary"
                }`}
              >
                <i className="fa fa-user-plus text-white"></i>
              </div>
              <div className="timeline-content">
                <span className="font-weight-bold">
                  Creating Parent Accounts
                </span>
              </div>
            </div>

            <div className="timeline-item">
              <div
                className={`timeline-media ${
                  progress.percentage >= 50 ? "bg-success" : "bg-secondary"
                }`}
              >
                <i className="fa fa-user-graduate text-white"></i>
              </div>
              <div className="timeline-content">
                <span className="font-weight-bold">
                  Creating Student Accounts
                </span>
              </div>
            </div>

            <div className="timeline-item">
              <div
                className={`timeline-media ${
                  progress.percentage >= 75 ? "bg-success" : "bg-secondary"
                }`}
              >
                <i className="fa fa-link text-white"></i>
              </div>
              <div className="timeline-content">
                <span className="font-weight-bold">Linking Relationships</span>
              </div>
            </div>

            <div className="timeline-item">
              <div
                className={`timeline-media ${
                  progress.percentage === 100 ? "bg-success" : "bg-secondary"
                }`}
              >
                <i className="fa fa-graduation-cap text-white"></i>
              </div>
              <div className="timeline-content">
                <span className="font-weight-bold">Enrolling in Program</span>
              </div>
            </div>
          </div>
        </div>
      </Box>
    </Box>
  );
};
